/**
 * @fileoverview Page Gate Components
 *
 * `PageGateContainer` wraps gated content: if the customer has already unlocked
 * the gate (server-rendered `unlocked` class) it restores storage keys; otherwise
 * it fetches and injects the `<page-gate>` form and reveals content on unlock.
 * `PageGate` is the gate form itself (password or email), submitting to the
 * Datajet app and unlocking on success.
 *
 * Debug mode: the "Debug page gate" theme setting (`settings.page_gate_debug`)
 * adds the `debug` class to both `<page-gate-container>` (theme.liquid) and
 * `<page-gate>` (page-gate-email/password sections), which logs lifecycle
 * events to the console.
 *
 * @example
 * <page-gate-container>
 *   <template id="gated-content">…</template>
 *   <script js-data type="application/json">{ "gate_url": "…" }</script>
 * </page-gate-container>
 */

import { debounce } from '@utilities/theme';

/**
 * @classdesc Wraps gated content and swaps it in once the gate is unlocked.
 * @extends HTMLElement
 */
export class PageGateContainer extends HTMLElement {
  #gated_content;
  #page_gate;
  #page_gate_data;
  #unlocked;

  constructor() {
    super();
    this.#page_gate_data = {};
  }

  async connectedCallback() {
    // unlocked is only set in server side render, this means a customer is present that has unlocked the gates
    // if a customer has unlocked the gate, the <page-gate> element won't render, so take the session data keys in the data and set them.
    this.#unlocked = this.classList.contains('unlocked');
    this.#page_gate_data = this.#setPageGateData();
    if (this.allowDebug()) {
      console.log('page-gate.js: PageGateContainer connectedCallback()', {
        unlocked: this.#unlocked,
        page_gate_data: this.#page_gate_data
      });
    }
    if(this.#unlocked){
      this.#setLocalStorageKeys();
      this.#setSessionStorageKeys();
    } else {
      this.#initFreshGate();
    }
  }

  /**
   * Determines if debugging is enabled for this page gate container
   * Returns true if the component has the debug class
   * @returns {boolean} Whether debugging is enabled
   */
  allowDebug() {
    return this.classList.contains('debug');
  }

  #initFreshGate = async () => {
    this.#setLockedContent();
    this.#setEventListeners();
    this.removeAttribute('data-url');
    await this.#injectGate();
  }

  #setSessionStorageKeys = () => {
    if(this.#page_gate_data.gate_session_storage_keys.length === 0) return;
    try {
      this.#page_gate_data.gate_session_storage_keys.forEach((session_storage_key) => {
        sessionStorage.setItem(session_storage_key, true);
      });
    } catch (error) {
      console.error('Error setting session storage data:', error);
    }
  }

  #setLocalStorageKeys = () => {
    if(this.#page_gate_data.gate_local_storage_keys.length === 0) return;
    try {
      this.#page_gate_data.gate_local_storage_keys.forEach((local_storage_key) => {
        localStorage.setItem(local_storage_key, true);
      });
    } catch (error) {
      console.error('Error setting local storage data:', error);
    }
  }

  #setPageGateData = () => {
    let tempData = {};
    const dataElement = this.querySelector('[js-data]');
    if (dataElement) {
      tempData = JSON.parse(dataElement.textContent);
      dataElement.remove();
    }
    return tempData;
  }

  #setLockedContent = () => {
    const template = this.querySelector('#gated-content');
    if (!template) {
      console.warn('Gated content template not found');
      return;
    }
    try {
      this.#gated_content = template.content.cloneNode(true);
      template.remove();
    } catch (error) {
      console.error('Error cloning template content:', error);
      return;
    }
  }

  #setEventListeners = () => {
    this.addEventListener('page-gate:unlocked', this.#openGate.bind(this));
  }

  #injectGate = async () => {
    try {
      const response = await fetch(this.#page_gate_data?.gate_url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const html = await response.text();
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(html, 'text/html');
      const parsedMainContent = parsedDocument.querySelector('#main-content');
      if (!parsedMainContent) {
        throw new Error('#main-content not found in fetched content');
      }
      const parsedGate = parsedMainContent.querySelector('page-gate');
      if (!parsedGate) {
        throw new Error('page-gate not found in fetched content');
      }
      this.#page_gate = parsedGate;
      this.innerHTML = '';
      this.appendChild(parsedMainContent);
      this.classList.remove('loading');
      this.classList.add('unlocked');
      if (this.allowDebug()) {
        console.log('page-gate.js: PageGateContainer #injectGate() gate injected', this.#page_gate);
      }
    } catch (error) {
      console.error('Error injecting gate:', error);
      this.classList.add('error');
    }
  }

  #isValidUnlockEvent = (event) => {
    return (
      event.target instanceof PageGate &&
      this.#page_gate &&
      event.target.id === this.#page_gate.id
    );
  }

  #unlockContent = () => {
    if (!this.#gated_content) {
      throw new Error('Unable to find gated content');
    }
    // Swap the content in first; session storage is a best-effort convenience
    // for future navigations and must not be able to block the swap.
    this.innerHTML = '';
    this.appendChild(this.#gated_content);
    this.#setSessionStorageKeys();
  }

  #openGate = (event) => {
    if (!this.#isValidUnlockEvent(event)) return;
    try {
      this.#unlockContent();
      if (this.allowDebug()) {
        console.log('page-gate.js: PageGateContainer #openGate() content unlocked', event);
      }
    } catch (error) {
      console.error('Error in openGate method:', error);
    }
  }
}

if (!customElements.get('page-gate-container')) {
  customElements.define('page-gate-container', PageGateContainer);
}

/**
 * @classdesc Password/email gate form that unlocks gated content on success.
 * @extends HTMLElement
 */
export class PageGate extends HTMLElement {
  #form;
  #password_input;
  #email_input;
  #required_checkboxes
  #submit_button;
  #response_container;
  #gate_data;
  #request_body;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#gate_data = {};
    this.#request_body = {
      properties: {
        payload: {}
      }
    };
    this.#init();
  }

  /**
   * Determines if debugging is enabled for this page gate
   * Returns true if the component has the debug class
   * @returns {boolean} Whether debugging is enabled
   */
  allowDebug = () => {
    return this.classList.contains('debug');
  }

  #init = () => {
    this.#gate_data = this.#setGateData();
    if (this.allowDebug()) {
      console.log('page-gate.js: PageGate #init() gate_data', this.#gate_data);
    }
    this.#form = this.querySelector('form');
    this.#submit_button = this.#form?.querySelector('button[type="submit"]');
    this.#response_container = this.#form?.querySelector('.response-container');
    // password_input only on password forms
    this.#password_input = this.#form?.querySelector('input[js-gate-password]');
    // email_input only on email forms
    this.#email_input = this.#form?.querySelector('input[js-gate-email]');
    this.#required_checkboxes = this.#form?.querySelectorAll('input[type="checkbox"][js-gate-required-checkbox]');
    // events
    this.#setEventListeners();
    if(this.#gate_data?.type == "email_form_page_gate"){
      this.#setListrakCode();
      this.#setEmailFromURL();
    } else if(this.#gate_data?.type == "password_form_page_gate"){
      this.#setPasswordFromURL();
    }
    this.#checkLocalStorage();
    this.#updateSubmitButtonState();
  }

  #setGateData = () => {
    let tempData = {};
    const dataElement = this.querySelector('[js-data]');
    if (dataElement) {
      tempData = JSON.parse(dataElement.textContent);
      dataElement.remove();
    }
    return tempData;
  }

  getData = () => {
    return this.#gate_data;
  }

  #setListrakCode = () => {
    // Listrak recommended format for async _ltk object listener
    // See: https://help.listrak.com/en/articles/4820523-javascript-website-integration-installation
    (function(d) {
      if (typeof _ltk == "undefined") {
        if (document.addEventListener) document.addEventListener("ltkAsyncListener", function() {
          _ltk_util.ready(d)
        });
        else {
          e = document.documentElement;
          e.ltkAsyncProperty = 0;
          e.attachEvent("onpropertychange", function(e) {
            if (e.propertyName == "ltkAsyncProperty") {
              _ltk_util.ready(d)
            }
          })
        }
      } else {
        _ltk_util.ready(d)
      }
    })(() => {
      _ltk.Signup.New(`${this.#gate_data?.listrak_subscribe_id}`, `${this.#email_input?.id}`, _ltk.Signup.TYPE.CLICK, `${this.#submit_button?.id}`);
    });
  }

  #setEventListeners = () => {
    this.#form?.addEventListener('submit', this.#handleFormSubmit.bind(this));
    this.#password_input?.addEventListener('input', debounce(this.#handleInputChange.bind(this), 300));
    this.#email_input?.addEventListener('input', debounce(this.#handleInputChange.bind(this), 300));
    this.#required_checkboxes?.forEach(checkbox => {
      checkbox.addEventListener('change', this.#handleInputChange.bind(this));
    });
  }

  #handleInputChange = () => {
    this.#updateSubmitButtonState();
  }

  #areAllRequiredCheckboxesChecked = () => {
    return Array.from(this.#required_checkboxes || []).every(checkbox => checkbox.checked);
  }

  #updateSubmitButtonState = () => {
    if (this.#submit_button) {
      const isInputValid = this.#password_input?.value.trim() || this.#email_input?.value.trim();
      const areCheckboxesValid = this.#areAllRequiredCheckboxesChecked();
      const isFormValid = isInputValid && areCheckboxesValid;

      this.#submit_button.disabled = !isFormValid;
      this.#submit_button.setAttribute('aria-disabled', (!isFormValid).toString());
    }
  }

  #checkLocalStorage = () => {
    const hasLocalStorageKey = this.#gate_data.linked_local_storage_keys.some((linked_local_storage_key) => {
      if (localStorage.getItem(linked_local_storage_key)) {
        return true;
      }
      return false;
    });
    if (hasLocalStorageKey) {
      this.#unlockGate();
    }
  }

  #setPasswordFromURL = () => {
    if (this.#password_input) {
      const urlParams = new URLSearchParams(window.location.search);
      const passwordParam = urlParams.get('password');
      if (passwordParam) {
        this.#password_input.value = passwordParam;
        this.#updateSubmitButtonState();
        if (!this.#submit_button?.disabled) this.#handleFormSubmit();
      }
    }
  }

  #setEmailFromURL = () => {
    if (this.#email_input) {
      const urlParams = new URLSearchParams(window.location.search);
      const emailParam = urlParams.get('email');
      if (emailParam) {
        this.#email_input.value = emailParam;
        this.#updateSubmitButtonState();
      }
    }
  }

  #setRequestData = () => {
    this.#request_body.properties.payload.gate_id = this.#gate_data.id;
    if (this.#password_input) {
      this.#request_body.properties.payload.submission = this.#password_input.value;
    } else if (this.#email_input) {
      this.#request_body.properties.payload.submission = this.#email_input.value;
    }
    const customerIdInput = document.querySelector('.js-customer-id');
    if (customerIdInput) {
      this.#request_body.properties.payload.customer_id = customerIdInput.value;
    }
  }

  #handleFormSubmit = async (event) => {
    event?.preventDefault();
    if (this.#submit_button) {
      this.#submit_button.setAttribute('disabled', 'true');
      this.#submit_button.setAttribute('aria-disabled', 'true');
      this.#submit_button.classList.add('loading');
    }
    await this.#triggerTask();
    if (this.#submit_button) {
      this.#updateSubmitButtonState();
      this.#submit_button.classList.remove('loading');
    }
  }

  #triggerTask = async () => {
    if (Object.keys(this.#gate_data).length === 0) {
      console.error('Task data is empty');
      this.#handleResponse({ status: 400, body: 'Task data is empty' });
      return;
    }
    this.#setRequestData();
    if (this.allowDebug()) {
      console.log('page-gate.js: PageGate #triggerTask() request_body', this.#request_body);
    }
    try {
      const response = await fetch(`${window.theme.routes.root_url}apps/datajet/task/${this.#gate_data.datajet_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.#gate_data.datajet_token && { 'Token': `${this.#gate_data.datajet_token}` })
        },
        body: JSON.stringify({ ...this.#request_body })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const responseData = await response.json();
      if (this.allowDebug()) {
        console.log('page-gate.js: PageGate #triggerTask() response', { status: response.status, body: responseData });
      }
      this.#handleResponse({ status: response.status, body: responseData });
    } catch (error) {
      console.error('Error fetching data:', error);
      this.#handleResponse({ status: error.status || 500, body: error.message });
    }
  }

  #handleResponse = (response) => {
    if (!response) {
      console.error('Response is undefined');
      return;
    }
    if (!this.#response_container) {
      console.warn('Response container not found');
    } else {
      if (response.status === 429) {
        this.#response_container.innerHTML = `<p>Rate limit exceeded. Try again later.</p>`;
        return;
      }
      if (response.body && response.body.return_value) {
        this.#response_container.innerHTML = `<pre>${response.body.return_value.message}</pre>`;
      } else {
        console.error('Unexpected response format:', response);
        this.#response_container.innerHTML = `<p>An unexpected error occurred. Please try again.</p>`;
      }
    }
    if (response.body && response.body.return_value && response.body.return_value.message === 'access granted') {
      this.#proceedWithUnlock();
    }
  }

  #proceedWithUnlock = () => {
    if (this.allowDebug()) {
      console.log('page-gate.js: PageGate #proceedWithUnlock() access granted, unlocking gate', this.#gate_data);
    }
    // Unlock first: swapping in the content is the whole point of a successful
    // submission and must happen regardless of storage support/availability.
    // The storage keys below only persist the unlock for future visits
    // (private browsing, quota limits, or blocked storage should never
    // prevent the customer from seeing the content they just unlocked).
    try {
      this.#unlockGate();
    } catch (error) {
      console.error('Error during unlock process:', error);
    }
    this.#setLocalStorageKeys();
    this.#setSessionStorageKeys();
  }

  #setLocalStorageKeys = () => {
    if(this.#gate_data.linked_local_storage_keys.length === 0) {
      return;
    }
    try {
      this.#gate_data.linked_local_storage_keys.forEach((local_storage_key) => {
        localStorage.setItem(local_storage_key, 'true');
      });
    } catch (error) {
      console.error('Error setting local storage data:', error);
    }
  }

  #setSessionStorageKeys = () => {
    if(this.#gate_data.linked_session_storage_keys.length === 0) {
      return;
    }
    try {
      this.#gate_data.linked_session_storage_keys.forEach((session_storage_key) => {
        sessionStorage.setItem(session_storage_key, 'true');
      });
    } catch (error) {
      console.error('Error setting session storage data:', error);
    }
  }

  #unlockGate =() => {
    try {
      const event = new CustomEvent('page-gate:unlocked', {
        bubbles: true
      });
      this.dispatchEvent(event);
    } catch (error) {
      console.error('Error in unlockGate:', error);
      throw error; // Re-throw to be caught in #proceedWithUnlock
    }
  }
}

if (!customElements.get('page-gate')) {
  customElements.define('page-gate', PageGate);
}
